import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { type XGridProps } from "@material-ui/x-grid";
import XGrid from "./StyledXGrid";

import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import { formatZonedDate } from "src/utils/timezone";
import { getZonedGridDateOperators } from "src/utils/gridZonedDateFilters";

function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
}

function EnhacedXGrid({
  rows,
  componentsProps,
  columns,
  ...props
}: XGridProps) {
  const [quickFilter, setQuickFilter] = useState<string>();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);

  const zonedColumns = useMemo(() => {
    if (!columns) return columns;
    return columns.map((col) => {
      if (col.type !== "date") return col;
      const out = { ...col };
      if (!out.valueFormatter && !out.renderCell) {
        out.valueFormatter = (p: { value: unknown }) =>
          formatZonedDate(p.value as Date, timeZone);
      }
      if (!out.filterOperators) {
        out.filterOperators = getZonedGridDateOperators(timeZone);
      }
      return out;
    });
  }, [columns, timeZone]);

  const filteredRows = useMemo(() => {
    if (quickFilter) {
      const searchRegex = new RegExp(escapeRegExp(quickFilter), "i");
      return rows.filter((row) => {
        return Object.keys(row).some((field) => {
          if (row[field] instanceof Date) {
            const formatted = formatZonedDate(row[field], timeZone);
            if (formatted && searchRegex.test(formatted)) return true;
            return false;
          }

          return row[field] ? searchRegex.test(row[field].toString()) : false;
        });
      });
    }

    return rows;
  }, [rows, quickFilter, timeZone]);

  return (
    <XGrid
      {...props}
      columns={zonedColumns}
      rows={filteredRows}
      componentsProps={{
        ...componentsProps,
        toolbar: {
          ...componentsProps?.toolbar,
          quickFilter,
          onQuickFilterChange: (value) => setQuickFilter(value),
        },
      }}
    />
  );
}

export default EnhacedXGrid;
